import React, { useState } from "react";
import { createPortal } from "react-dom";
import StatusBadge from "./StatusBadge";
import styles from "./VendorList.module.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

// 🛠️ FIX: Generic S3 URL correction function
const getValidS3Url = (url) => {
  if (!url) return null;
  return url.replace("s3.ap-south-1", "s3-ap-southeast-1");
};
const getValidDeckUrl = getValidS3Url;

function VendorList({ vendors: filteredVendors }) {
  const [selected, setSelected] = useState(null);
  const [redeemEmail, setRedeemEmail] = useState("");
  const [redeemMessage, setRedeemMessage] = useState("");
  const [serviceMessages, setServiceMessages] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessages, setSuccessMessages] = useState([]);

  // ✅ Fetch full vendor details for the modal
  const openVendorModal = async (vendorId) => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_BASE}/api/ssn/vendors/${vendorId}/`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        }
      });
      if (!res.ok) { const e = new Error(`HTTP ${res.status}`); e.status = res.status; throw e; }
      const data = await res.json();
      setSelected(data);
      setErrorMessage("");
      setSuccessMessages([]);
      setServiceMessages({});
    } catch (err) {
      console.error("Error fetching vendor details:", err);
    }
  };

  // ✅ Redeem service with combined message
  const handleRedeem = (service, vendor) => {
    const token = localStorage.getItem('token');
    
    const combinedMessage = `${
      redeemMessage ? redeemMessage + "\n\n---\nGeneral Message Above\n\n" : ""
    }${serviceMessages[service.id] || ""}`;

    if (!redeemEmail) {
      setErrorMessage("❌ Email is required to redeem this service.");
      setSuccessMessages([]);
      return;
    }

    fetch(`${API_BASE}/api/ssn/vendors/${vendor.id}/services/${service.id}/redeem/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify({ email: redeemEmail, message: combinedMessage }),
    })
      .then((res) => {
        if (!res.ok) {
          return res.json().then((err) => {
            throw new Error(err.error || "Failed to redeem service");
          });
        }
        return res.json();
      })
      .then((data) => {
        setErrorMessage("");
        setSuccessMessages(prev => [
          ...prev,
          <div key={data.id || Date.now()}>
            ✅ Redeem request sent for: {service.service_name}<br />
            Status: <StatusBadge status={data.status || 'pending'} />
            <p>We'll review your request and get back to you soon.</p>
          </div>
        ]);
        setServiceMessages((prev) => ({ ...prev, [service.id]: "" }));
      })
      .catch((err) => {
        setSuccessMessages([]);
        setErrorMessage("❌ " + err.message);
        console.error(err);
      });
  };

  return (
    <div className={styles.vendorListContainer}>
      <div className={styles.vendorList}>
        {filteredVendors
          .filter((vendor) => vendor && vendor.name)
          .map((vendor) => (
            <div
              key={vendor.id}
              className={styles.vendorCard}
              onClick={() => openVendorModal(vendor.id)}
              data-vendor-id={vendor.id}
            >
              {vendor.logo && (
                <img
                  src={getValidS3Url(vendor.logo)}
                  alt={vendor.name}
                  className={styles.vendorLogo}
                />
              )}
              <div className={styles.vendorName}>{vendor.name}</div>
              <div className={styles.vendorType}>{vendor.category}</div>

              <div className={styles.vendorDescription}>
                {vendor.description
                  ? vendor.description.slice(0, 140)
                  : "No description available."}
              </div>

              {vendor.requirements && (
                <div className={styles.vendorMeta}>
                  <strong>Requirements:</strong> {vendor.requirements}
                </div>
              )}
              {vendor.timeline && (
                <div className={styles.vendorMeta}>
                  <strong>Timeline:</strong> {vendor.timeline}
                </div>
              )}
            </div>
          ))}
        {filteredVendors.length === 0 && <div>No vendors found.</div>}
      </div>

      {/* ✅ Full Page Vendor View - Using Portal */}
      {selected && createPortal(
        <div className={styles.fullPageVendorView}>
          <div className={styles.modalContainer}>
            {/* Vendor Detail Header */}
            <div className={styles.vendorDetailHeader}>
              <h2>{selected.name}</h2>
              <button 
                onClick={() => {
                  setSelected(null);
                  setErrorMessage("");
                  setSuccessMessages([]);
                  setServiceMessages({});
                  setRedeemEmail("");
                  setRedeemMessage("");
                }} 
                className={styles.vendorDetailCloseBtn}
              >
                ×
              </button>
            </div>

            <div className={styles.vendorDetailContent}>
            {/* Vendor Info Section */}
            <div className={styles.vendorInfoSection}>
              {selected.logo && (
                <div className={styles.logoContainer}>
                  <img
                    src={getValidS3Url(selected.logo)}
                    alt={selected.name}
                    className={styles.vendorLogo}
                  />
                </div>
              )}
              
              <div className={styles.vendorDetails}>
                <p className={styles.vendorDescription}>
                  {selected.description || "No description available"}
                </p>
                <div className={styles.categoryInfo}>
                  <span className={styles.categoryLabel}>Category:</span>
                  <span className={styles.categoryValue}>{selected.category}</span>
                </div>
                
                {selected.requirements && (
                  <div className={styles.requirementInfo}>
                    <span className={styles.requirementLabel}>Requirements:</span>
                    <span className={styles.requirementValue}>{selected.requirements}</span>
                  </div>
                )}
                
                {selected.timeline && (
                  <div className={styles.timelineInfo}>
                    <span className={styles.timelineLabel}>Timeline:</span>
                    <span className={styles.timelineValue}>{selected.timeline}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Messages Section */}
            {(errorMessage || successMessages.length > 0) && (
              <div className={styles.messagesSection}>
                {errorMessage && (
                  <div className={styles.errorMessage}>{errorMessage}</div>
                )}
                {successMessages.length > 0 && (
                  <div className={styles.successMessagesContainer}>
                    {successMessages.map((msg, index) => (
                      <div key={`success-${index}`} className={styles.successMessage}>
                        {msg}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Contact Form Section */}
            <div className={styles.contactFormSection}>
              <h3 className={styles.sectionTitle}>Contact Information</h3>
              <div className={styles.formFields}>
                <div className={styles.fieldGroup}>
                  <label htmlFor="redeemEmail" className={styles.fieldLabel}>Your Email:</label>
                  <input
                    id="redeemEmail"
                    type="email"
                    value={redeemEmail}
                    onChange={(e) => setRedeemEmail(e.target.value)}
                    placeholder="your.email@example.com"
                    className={styles.inputField}
                  />
                </div>
                <div className={styles.fieldGroup}>
                  <label htmlFor="redeemMessage" className={styles.fieldLabel}>Message (optional):</label>
                  <textarea
                    id="redeemMessage"
                    value={redeemMessage}
                    onChange={(e) => setRedeemMessage(e.target.value)}
                    placeholder="Enter your general message here..."
                    className={styles.textareaField}
                  />
                </div>
              </div>
            </div>

            {/* Services Section */}
            <div className={styles.servicesSection}>
              <h3 className={styles.sectionTitle}>Services</h3>
              {selected.services && selected.services.length > 0 ? (
                <div className={styles.servicesList}>
                  {selected.services.map((service) => (
                    <div key={service.id} className={styles.serviceItem} data-service-id={service.id}>
                      <div className={styles.serviceInfo}>
                        <strong className={styles.serviceName}>{service.service_name}</strong>
                        <p className={styles.serviceDescription}>{service.short_description}</p>
                        <textarea
                          placeholder="Add a message for this specific service..."
                          value={serviceMessages[service.id] || ""}
                          onChange={(e) =>
                            setServiceMessages((prev) => ({
                              ...prev,
                              [service.id]: e.target.value,
                            }))
                          }
                          className={styles.serviceTextarea}
                        />
                      </div>
                      <button
                        onClick={() => handleRedeem(service, selected)}
                        className={styles.redeemBtn}
                      >
                        Send Request
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className={styles.noServicesText}>No services available.</p>
              )}
            </div>

            {/* Deck Download Section */}
            {selected.deck_url && (
              <div className={styles.deckSection}>
                <a
                  href={getValidDeckUrl(selected.deck_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.downloadBtn}
                >
                  Download Deck
                </a>
              </div>
            )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default VendorList;
